import MessageList, { Message } from "@/components/message/message-list";
import { useParams, useSearchParams } from "react-router-dom";
import { useMessagesQuery } from "@/hooks/services/queries/ai-agent/use-messages-query";
import MessageInputMedium from "@/components/message/message-input-medium";
import { useRef, useEffect, useCallback, useState } from "react";
import { useDemoChatStreamMutation } from "@/hooks/services/mutations/ai-agent/use-demo-chat-stream-mutation";
import MessageNav from "../message/message-nav";
import { useAiAgentStore, DEFAULT_CHAT_SOURCE_SETTINGS } from "@/stores/ai-agent";
import AiAgentNotEnoughCredit from "@/components/ai/ai-agent-not-enough-credit";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { instance } from "@/apis";
import useChatStreamActiveQuery from "@/hooks/services/queries/ai-agent/use-chat-stream-active-query";

interface AiChatContainerProps {
  sidebarOpen?: boolean;
}

const AiChatContainer = ({ sidebarOpen }: AiChatContainerProps) => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const bottomRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const prevMessageCountRef = useRef(0);
  const prevLastMessageLineCountRef = useRef(0);

  // 현재 화면에 보이는 USER 메시지 인덱스 (스크롤 기준)
  const [activeUserMessageIndex, setActiveUserMessageIndex] =
    useState<number>(0);

  const chatId = id; // undefined 가능 (새 채팅인 경우)
  const messageId = searchParams.get("messageId");
  const queryClient = useQueryClient();
  // 채팅방 진입 시 Light 버전, 클릭하면 Medium 버전으로 전환
  const [isInputExpanded, setIsInputExpanded] = useState(false);
  const selectedJob = useAiAgentStore((state) => state.selectedJob);
  const clearSelectedJob = useAiAgentStore((state) => state.clearSelectedJob);
  const showCreditError = useAiAgentStore((state) => state.showCreditError);
  const setShowCreditError = useAiAgentStore((state) => state.setShowCreditError);
  const isThisChatStreaming = useAiAgentStore((state) => state.streamingChatIds.has(chatId || ""));
  const isStreamComplete = useAiAgentStore((state) => state.isStreamComplete);
  const setStreamComplete = useAiAgentStore((state) => state.setStreamComplete);
  const clearProgressList = useAiAgentStore((state) => state.clearProgressList);
  const chatSourceSettings = useAiAgentStore(
    (state) => (chatId ? state.chatSourceSettingsMap.get(chatId) : undefined) ?? DEFAULT_CHAT_SOURCE_SETTINGS
  );
 // 채팅방 변경 또는 페이지 이동 시 selectedJob 초기화
  useEffect(() => {
    clearSelectedJob();
    return () => {
      clearSelectedJob();
    };
  }, [chatId, clearSelectedJob]);

  // 메시지 쿼리 (캐시 변경 자동 감지)
  // chatId가 없으면 "temp"를 사용하여 새 채팅의 optimistic update를 표시
  const { data: messages } = useMessagesQuery({
    chatId: chatId || "temp",
    enabled: true, // 항상 활성화 (캐시 변경 감지용)
  });

  // 새로고침 시 서버 스트리밍 active 여부 폴링 (이 채팅방이 로컬 SSE 없을 때만)
  const { data: streamActiveData } = useChatStreamActiveQuery(chatId, !isThisChatStreaming);

  // active: true → false 전환 감지 → 완료 처리 + 최신 메시지 반영
  const wasStreamActiveRef = useRef(false);
  useEffect(() => {
    if (streamActiveData?.active === true) {
      wasStreamActiveRef.current = true;
    } else if (streamActiveData?.active === false && wasStreamActiveRef.current) {
      wasStreamActiveRef.current = false;
      if (!chatId) return;
      // 로컬 SSE 스트리밍 중이면 캐시 덮어쓰기 방지 (in-flight 폴 응답 race condition 방어)
      if (useAiAgentStore.getState().streamingChatIds.has(chatId)) return;
      // 완료 판정 강제 보장 (서버 poll 경로)
      setStreamComplete(chatId);
      clearProgressList(chatId);
      instance.get(`/api/df/user/chat/${chatId}`).then((res) => {
        const data = res?.data?.data;
        if (Array.isArray(data) && data.length > 0) {
          queryClient.setQueryData(["messages", chatId], data);
        }
      }).catch(() => {});
    }
  }, [streamActiveData?.active, chatId, queryClient, setStreamComplete, clearProgressList]);

  const { mutate: sendStreamMessage } = useDemoChatStreamMutation(chatId);

  const handleSend = useCallback(
    (text: string, files?: File[]) => {
      // TODO: 이미지 기능은 나중에 추가 예정
      sendStreamMessage({
        chatId, // undefined일 경우 서버가 자동으로 새 채팅방 생성
        newMessage: text,
        deepResearch: chatSourceSettings.deepResearch,
        source: {
          web_search_enabled: chatSourceSettings.webSearchEnabled,
          private_kb_enabled: chatSourceSettings.privateKbEnabled,
          biomcp_enabled: chatSourceSettings.biomcpEnabled,
          paper_search_preferred: chatSourceSettings.academicJournalEnabled,
        },
        files,
        selectedJob: selectedJob?.jobPlanDTO.id,
        sdfJobName: selectedJob?.analysisNo,
      });
      setIsInputExpanded(false);
    },
    [chatId, sendStreamMessage, chatSourceSettings, selectedJob]
  );

  useEffect(() => {
    const count = messages?.length ?? 0;
    const isNewMessage = count > prevMessageCountRef.current;
    prevMessageCountRef.current = count;

    const lastMsg = messages?.[messages.length - 1];
    const lastMsgContent = lastMsg?.message ?? "";
    const currentLineCount = (lastMsgContent.match(/\n/g) ?? []).length;
    const isNewLine = currentLineCount > prevLastMessageLineCountRef.current;
    prevLastMessageLineCountRef.current = isNewLine ? currentLineCount : prevLastMessageLineCountRef.current;

    // 새 메시지가 추가되거나 줄바꿈이 생겼을 때만 스크롤
    if (isNewMessage || isNewLine) {
      prevLastMessageLineCountRef.current = currentLineCount;
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Credit 에러 Toast 표시
  useEffect(() => {
    if (showCreditError) {
      toast.custom(
        () => <AiAgentNotEnoughCredit />,
        {
          position: "bottom-center",
          duration: 5000,
        }
      );
      setShowCreditError(false); // 표시 후 리셋
    }
  }, [showCreditError, setShowCreditError]);

  // 메시지 DOM 참조 수집용: index 기준으로 매핑
  const messageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  // 메시지 ID를 키로 하는 참조 맵 (스크롤용)
  const messageRefsById = useRef<Record<string, HTMLDivElement | null>>({});
  // 스크롤 대기 중인 메시지 ID
  const pendingScrollId = useRef<string | null>(null);
  // USER 메시지 요소들을 추적하기 위한 맵 (userIndex -> element)
  const userMessageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const lastActiveUserMessageIndexRef = useRef<number>(0);
  const scrollRafRef = useRef<number | null>(null);

  function updateActiveUserMessageIndexFromScroll() {
    const root = mainRef.current;
    if (!root) return;

    const rootRect = root.getBoundingClientRect();
    // `pt-[64px]` 아래에서 "현재 읽는 위치"를 판단하기 위한 기준선
    const activeLineY = rootRect.top + 72;

    const entries = Object.entries(userMessageRefs.current)
      .map(([key, el]) => ({ userIndex: Number(key), el }))
      .filter((x) => Number.isFinite(x.userIndex) && x.el)
      .sort((a, b) => a.userIndex - b.userIndex) as Array<{
      userIndex: number;
      el: HTMLDivElement;
    }>;

    if (entries.length === 0) return;

    let nextActiveIndex = 0;
    for (const { userIndex, el } of entries) {
      const top = el.getBoundingClientRect().top;
      // 기준선(activeLineY)을 지나간(= 시작이 위로 올라간) 마지막 USER 메시지를 active로 본다
      if (top <= activeLineY) nextActiveIndex = userIndex;
      else break;
    }

    if (lastActiveUserMessageIndexRef.current !== nextActiveIndex) {
      lastActiveUserMessageIndexRef.current = nextActiveIndex;
      setActiveUserMessageIndex(nextActiveIndex);
    }
  }

  useEffect(() => {
    const root = mainRef.current;
    if (!root) return;

    function onScroll() {
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = requestAnimationFrame(() => {
        updateActiveUserMessageIndexFromScroll();
      });
    }

    root.addEventListener("scroll", onScroll, { passive: true });
    // 최초 진입/메시지 변경 시에도 한 번 계산
    onScroll();

    return () => {
      root.removeEventListener("scroll", onScroll);
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
      scrollRafRef.current = null;
    };
  }, [messages]);

  function handleItemMount(
    message: Message,
    index: number,
    el: HTMLDivElement | null
  ) {
    messageRefs.current[index] = el;
    // 메시지 ID로도 참조 저장
    if (message.id && el) {
      messageRefsById.current[message.id] = el;

      // 이 메시지가 스크롤 대상이면 즉시 스크롤
      if (pendingScrollId.current === message.id) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        pendingScrollId.current = null;
        // 스크롤 후 쿼리 파라미터 제거
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("messageId");
          return newParams;
        });
      }
    }

    // USER 메시지인 경우 activeIndex 계산용 ref 등록/해제
    if (message.messageType === "USER") {
      // USER 메시지 목록에서의 인덱스 계산
      const allMessages = messages ?? [];
      let userIndex = 0;
      for (let i = 0; i < index; i++) {
        if (allMessages[i]?.messageType === "USER") {
          userIndex++;
        }
      }

      if (el) userMessageRefs.current[userIndex] = el;
      else delete userMessageRefs.current[userIndex];
    }
  }

  function scrollToMessage(_target: Message, index: number) {
    // 클릭한 메시지를 즉시 하이라이팅 (스크롤 기준 로직이 곧 동일하게 따라잡음)
    setActiveUserMessageIndex(index);

    // 사용자 메시지 목록에서의 index이므로, 전체 messages에서 같은 순서의 사용자 메시지의 실제 index를 찾아야 함
    const allMessages = messages ?? [];
    let userCounter = -1;
    let absoluteIndex = -1;
    for (let i = 0; i < allMessages.length; i++) {
      if (allMessages[i].messageType === "USER") {
        userCounter += 1;
        if (userCounter === index) {
          absoluteIndex = i;
          break;
        }
      }
    }
    if (absoluteIndex < 0) return;
    const el = messageRefs.current[absoluteIndex];
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // 메시지 ID 기반으로 스크롤하는 함수
  const scrollToMessageById = useCallback(
    (targetMessageId: string) => {
      // 먼저 ID 기반 참조 맵에서 찾기
      const elById = messageRefsById.current[targetMessageId];
      if (elById) {
        elById.scrollIntoView({ behavior: "smooth", block: "start" });
        // 스크롤 후 쿼리 파라미터 제거
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("messageId");
          return newParams;
        });
        return true;
      }

      // ID 기반 참조가 없으면 인덱스 기반으로 찾기 (폴백)
      const allMessages = messages ?? [];
      const targetIndex = allMessages.findIndex(
        (msg: Message) => msg.id === targetMessageId
      );

      if (targetIndex < 0) {
        console.warn(
          `Message with id ${targetMessageId} not found in messages array. Available IDs:`,
          allMessages.map((m: Message) => m.id)
        );
        return false;
      }

      const el = messageRefs.current[targetIndex];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        // 스크롤 후 쿼리 파라미터 제거
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("messageId");
          return newParams;
        });
        return true;
      }

      // DOM 요소가 아직 준비되지 않은 경우
      console.warn(
        `DOM element for message ${targetMessageId} at index ${targetIndex} not yet mounted`
      );
      return false;
    },
    [messages, setSearchParams]
  );

  // URL 쿼리 파라미터의 messageId가 변경되면 해당 메시지로 스크롤
  useEffect(() => {
    if (!messageId || !messages || messages.length === 0) {
      pendingScrollId.current = null;
      return;
    }

    // 먼저 즉시 스크롤 시도
    const success = scrollToMessageById(messageId);

    if (!success) {
      // 실패하면 스크롤 대기 목록에 추가 (handleItemMount에서 처리)
      pendingScrollId.current = messageId;

      // 메시지가 마운트되지 않았을 경우를 대비해 재시도
      let retryCount = 0;
      const maxRetries = 10;
      const retryInterval = 100;

      const tryScroll = () => {
        const retrySuccess = scrollToMessageById(messageId);
        if (retrySuccess) {
          pendingScrollId.current = null;
        } else if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(tryScroll, retryInterval);
        } else {
          // 최대 재시도 횟수 초과 시 대기 상태 해제
          pendingScrollId.current = null;
        }
      };

      // 초기 지연 후 첫 시도
      const timer = setTimeout(tryScroll, 200);
      return () => {
        clearTimeout(timer);
        pendingScrollId.current = null;
      };
    }
  }, [messageId, messages, scrollToMessageById]);

  return (
    <main
      ref={mainRef}
      className="relative w-full h-[calc(100vh-70px)] pt-[64px] overflow-y-auto overflow-x-hidden"
    >
      {/* container-type 은 답변 표가 채팅 페인 폭을 알기 위한 기준점. 안에 fixed 요소를 두면 기준이 바뀌니 portal 로 뺀다.
          data-table-pane 은 넓은 표가 스크롤로 넘어갈 수 있는 왼쪽 끝의 기준이다(TableScrollBox) */}
      <div
        data-table-pane=""
        className="flex flex-col items-center min-h-full [container-type:inline-size]"
      >
        <MessageList
          messages={messages}
          onItemMount={handleItemMount}
          pollLoading={(() => {
            // 이 채팅방 로컬 SSE 진행 중이면 숨김
            if (isThisChatStreaming) return undefined;
            // 완료 판정된 경우 숨김
            if (chatId && isStreamComplete(chatId)) return undefined;
            // 마지막 메시지가 빈 ASSISTANT이면 AssistantMessage가 자체 스피너를 렌더링하므로 제외
            const lastMsg = messages?.[messages.length - 1];
            if (lastMsg?.messageType === "ASSISTANT" && lastMsg?.message === "") return undefined;
            return streamActiveData;
          })()}
        />
        <div ref={bottomRef} />
        {!sidebarOpen && (
        <MessageNav
          messages={
            messages?.filter((msg: Message) => msg.messageType === "USER") ||
            []
          }
          activeIndex={activeUserMessageIndex}
          onSelect={scrollToMessage}
        />
        )}

        <div className="sticky bottom-5 mt-auto w-full flex justify-center px-4">
          <MessageInputMedium
            chatId={chatId || ""}
            expanded={isInputExpanded}
            onExpand={() => setIsInputExpanded(true)}
            onSend={handleSend}
            autoFocus={isInputExpanded}
          />
        </div>
      </div>
    </main>
  );
};

export default AiChatContainer;
